// The connection to the Gatherly API (backend/). In production the API is
// served from the same address under /api; during development `npm start`
// forwards /api to the backend's gateway (see "proxy" in package.json).
//
// Signing in returns two tokens:
//   token          sent with every request (Authorization: Bearer …); it
//                  expires after a while
//   refreshToken   swapped at POST /auth/refresh for a new pair when the
//                  token has expired; each one works only once
// Both are kept in this browser's storage, so every tab shares them.
export const API_BASE = process.env.REACT_APP_API_URL || "/api";

const TOKEN_KEY = "gatherly_token";
const REFRESH_KEY = "gatherly_refresh";

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // ignore storage failures (private browsing, etc.)
  }
};

export const getToken = () => read(TOKEN_KEY);
export const getRefreshToken = () => read(REFRESH_KEY);

// Called when the session ends without the host signing out (it expired, or
// was ended from another device), so the app can show the login page.
const endListeners = new Set();
export const onSessionEnd = (listener) => {
  endListeners.add(listener);
  return () => endListeners.delete(listener);
};

// Stores the tokens from a sign-in (or a refresh); null signs out.
export const setSession = (session) => {
  write(TOKEN_KEY, session?.token);
  write(REFRESH_KEY, session?.refreshToken);
};

export const setToken = (token) => setSession(token ? { token, refreshToken: getRefreshToken() } : null);

const endSession = () => {
  setSession(null);
  endListeners.forEach((listener) => listener());
};

// Errors carry a translation key as their message (as sent by the API), so
// pages can show t(error.message); `status` holds the HTTP status.
export const apiError = (key, status = 0) => Object.assign(new Error(key), { status });

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Swaps the refresh token for new tokens. `stale` is the token a request was
// refused with: if another tab or request has replaced it meanwhile, the new
// one is used instead. Resolves to true when there is a fresh token to retry
// with. Concurrent calls share one request.
let refreshing = null;
export function refreshSession(stale) {
  const current = getToken();
  if (current && current !== stale) return Promise.resolve(true);
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const response = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (response.ok) {
          setSession(await response.json());
          return true;
        }
        if (response.status !== 401) return false;
        // Another tab may have used this refresh token a moment earlier; its
        // new tokens then arrive in storage shortly.
        for (const delay of [0, 1000]) {
          await wait(delay);
          if (getRefreshToken() !== refreshToken) return Boolean(getToken());
        }
        endSession();
        return false;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

// Options: headers, form (a FormData body) and anonymous (send no token, e.g.
// signing in: a wrong password answers 401 too, which is not an expired
// session).
const send = (method, path, body, { headers = {}, form, anonymous } = {}) => {
  const token = anonymous ? null : getToken();
  return fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(form || body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: form || (body === undefined ? undefined : JSON.stringify(body)),
  });
};

export async function request(method, path, body, options = {}) {
  let response;
  try {
    const sentWith = options.anonymous ? null : getToken();
    response = await send(method, path, body, options);
    // An expired token: renew it once and try again.
    if (response.status === 401 && sentWith && (await refreshSession(sentWith))) {
      response = await send(method, path, body, options);
    }
    // Still refused (e.g. a sign-in from before the refresh tokens existed).
    if (response.status === 401 && sentWith && getToken() === sentWith) endSession();
  } catch {
    throw apiError("errors.network");
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) throw apiError(data?.error || "errors.server", response.status);
  return data;
}

export const isNotFound = (error) => error?.status === 404;
